import type { ReactNode } from 'react';

import { createCagePageUrl } from '@/utils/urlGenerator';

import { OpenLinkButton } from './cells/LocationOverviewCageLinkCell';
import LocationOverviewFishGroupCell from './cells/LocationOverviewFishGroupCell';
import { NO_VALUE, type OverviewCellContext } from './overviewCageCells';
import type { LocationOverviewColumnKey, LocationOverviewRow } from './types';

type CohortRow = Extract<LocationOverviewRow, { status: 'closedFishGroup' }>;

/**
 * One cell of a departed cohort's row: the pen it lived in, its name, and a way in.
 *
 * Every other column shows the table's `---`, matching how a live pen renders a
 * metric it has no value for. The alternative — blank cells — was more precise
 * about *why* there is no number, and read as a rendering fault.
 */
export function renderCohortCell(
    key: LocationOverviewColumnKey,
    row: CohortRow,
    context: OverviewCellContext
): ReactNode {
    const { closedFishGroup } = row;
    const { clientId, locationId, t } = context;

    switch (key) {
        case 'cage': {
            // `fg` scopes the cage pages to this cohort instead of the pen's current
            // occupant. It travels in the URL rather than in client state so the view is
            // linkable and survives a refresh — and because the cage context cannot infer
            // a closed cohort, knowing only who is in the pen now. Same treatment as a live
            // pen's link — see `LocationOverviewCageLinkCell`.
            const cohortUrl = createCagePageUrl(
                clientId,
                locationId,
                closedFishGroup.cageId,
                '/dashboard',
                closedFishGroup.fishGroupId
            );
            // Label says the pen, tooltip says the cohort — between them the row still
            // answers both questions once the `Name` column is gone.
            return (
                <OpenLinkButton
                    to={cohortUrl}
                    tooltip={`${t('common.actions.goTo')} ${closedFishGroup.fishGroupName}`}
                    label={context.showsNameColumn ? undefined : closedFishGroup.cageName}
                />
            );
        }
        case 'name':
            return closedFishGroup.cageName;
        case 'fishGroup':
            return (
                <LocationOverviewFishGroupCell
                    name={closedFishGroup.fishGroupName}
                    fromDate={closedFishGroup.fromDate}
                    toDate={closedFishGroup.toDate}
                />
            );
        default:
            return NO_VALUE;
    }
}
